/**
 * RegistroGrupoMulti
 *
 * Captura múltiple de calificaciones para un grupo:
 * todas las columnas (Parcial 1, Parcial 2, Parcial 3, Final, Extraordinario)
 * visibles y editables al mismo tiempo en una sola tabla.
 *
 * Persistencia (UPSERT) via /escolar/calificaciones/guardar-multi/
 *  - Si el par (id_estudiante_grupo, tipo_evaluacion) ya existe: UPDATE
 *  - Si no existe: INSERT
 *  - Si una celda se deja vacía: NO se sobrescribe (se omite ese parcial)
 *
 * Reutiliza estilos de RegistroCalificacionesScreen para mantener
 * exactamente el mismo diseño visual.
 */

import React, { useEffect, useMemo, useState } from 'react';
import styles from '../RegistroCalificacionesScreen/RegistroCalificaciones.module.css';
import { getEstudiantesGrupo, notifyCalificacionesUpdated } from '../../api/calificaciones';
import { guardarCalificacionesMulti } from '../../api/calificacionesEscolar';

const PARCIALES = [
  { key: 'PARCIAL1', label: 'Parcial 1', dictKey: 'PARCIAL 1' },
  { key: 'PARCIAL2', label: 'Parcial 2', dictKey: 'PARCIAL 2' },
  { key: 'PARCIAL3', label: 'Parcial 3', dictKey: 'PARCIAL 3' },
  { key: 'FINAL', label: 'Final', dictKey: 'FINAL' },
  { key: 'EXTRAORDINARIO', label: 'Extraordinario', dictKey: 'EXTRAORDINARIO' },
];

// Tabla oficial de redondeo de Calificación Final por rangos.
// 0-5.9 -> 5 | 6.0-6.9 -> 6 | 7.0-7.4 -> 7 | 7.5-8.4 -> 8 |
// 8.5-9.4 -> 9 | 9.5-10 -> 10
function redondearFinalPorRangos(promedio) {
  if (promedio === null || promedio === undefined || isNaN(promedio)) return '';
  const p = Number(promedio);
  if (p < 6) return 5;
  if (p < 7) return 6;
  if (p < 7.5) return 7;
  if (p < 8.5) return 8;
  if (p < 9.5) return 9;
  return 10;
}

// Calcula la Calificación Final automática a partir de P1, P2, P3.
// Devuelve string vacío cuando falta cualquiera de los tres parciales.
function calcularFinalAuto(p1, p2, p3) {
  const a = parseInt(p1, 10);
  const b = parseInt(p2, 10);
  const c = parseInt(p3, 10);
  if ([a, b, c].some((n) => isNaN(n))) return '';
  const promedio = (a + b + c) / 3;
  const final = redondearFinalPorRangos(promedio);
  return final === '' ? '' : String(final);
}

// Normaliza el nombre de sección para detectar "Preparatoria" tolerando
// acentos, mayúsculas/minúsculas y variantes (BACHILLERATO).
function esSeccionPreparatoria(nombre) {
  if (!nombre) return false;
  const s = String(nombre)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
  return s.includes('PREPARATORIA') || s.includes('BACHILLERATO');
}

function RegistroGrupoMulti({ grupo, onRegresar, onAbrirReporte }) {
  const [estudiantes, setEstudiantes] = useState([]);
  const [calificaciones, setCalificaciones] = useState({}); // { [id_eg]: { PARCIAL1, PARCIAL2, ..., observaciones } }
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [ordenamiento, setOrdenamiento] = useState({ campo: 'nombre', direccion: 'asc' });
  const [paginaActual, setPaginaActual] = useState(1);
  const REGISTROS_POR_PAGINA = 15;

  // Render condicional de columna Extraordinario: solo Preparatoria.
  const mostrarExtraordinario = useMemo(
    () => esSeccionPreparatoria(grupo?.seccion_nombre),
    [grupo]
  );

  // Lista efectiva de columnas a renderizar y a enviar al backend.
  const columnas = useMemo(
    () =>
      PARCIALES.filter(
        (p) => p.key !== 'EXTRAORDINARIO' || mostrarExtraordinario
      ),
    [mostrarExtraordinario]
  );

  // Cargar estudiantes con TODAS las calificaciones de TODOS los parciales
  useEffect(() => {
    const cargar = async () => {
      if (!grupo?.id_grupo) return;
      try {
        setLoading(true);
        // Pedimos cualquier tipo (el backend responde con calificaciones_dict completo
        // independientemente del tipo solicitado).
        const data = await getEstudiantesGrupo(grupo.id_grupo, 'PARCIAL1');
        const lista = data.estudiantes || [];
        console.log('[RegistroGrupoMulti] Estudiantes cargados:', lista);
        setEstudiantes(lista);

        const inicial = {};
        lista.forEach((est) => {
          const dict = est.calificaciones || {};
          const fila = { observaciones: '' };
          PARCIALES.forEach(({ key, dictKey }) => {
            const cal = dict[dictKey];
            // FIX 2026-08-14: Log para diagnosticar calificaciones NULL o faltantes
            if (!cal) {
              console.log(`[RegistroGrupoMulti] Sin calificaciones para ${est.id_estudiante_grupo} en ${dictKey}`);
            } else if (cal.calificacion === null || cal.calificacion === undefined) {
              console.warn(`[RegistroGrupoMulti] ADVERTENCIA: ${dictKey} para ${est.id_estudiante_grupo} es NULL`);
            }
            if (cal && cal.calificacion !== null && cal.calificacion !== undefined) {
              fila[key] = String(cal.calificacion);
            } else {
              fila[key] = '';
            }
            // Tomar observaciones del primer parcial que las tenga
            if (cal?.observaciones && !fila.observaciones) {
              fila.observaciones = cal.observaciones;
            }
          });
          // Final siempre se deriva automáticamente de P1/P2/P3.
          // Si los 3 parciales existen, sobreescribimos el valor cargado
          // para garantizar consistencia con la nueva regla de cálculo.
          const finalAuto = calcularFinalAuto(
            fila.PARCIAL1,
            fila.PARCIAL2,
            fila.PARCIAL3
          );
          if (finalAuto !== '') {
            fila.FINAL = finalAuto;
          }
          inicial[est.id_estudiante_grupo] = fila;
        });
        setCalificaciones(inicial);
      } catch (error) {
        console.error('[RegistroGrupoMulti] Error cargando estudiantes:', error);
        setMensaje({ tipo: 'error', texto: 'Error al cargar estudiantes del grupo' });
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [grupo]);

  const estudiantesOrdenados = useMemo(() => {
    if (!estudiantes.length) return [];
    return [...estudiantes].sort((a, b) => {
      let va, vb;
      if (ordenamiento.campo === 'nombre') {
        va = (a.nombre || '').toLowerCase();
        vb = (b.nombre || '').toLowerCase();
      } else {
        va = a.matricula || '';
        vb = b.matricula || '';
      }
      if (ordenamiento.direccion === 'asc') return va < vb ? -1 : va > vb ? 1 : 0;
      return va > vb ? -1 : va < vb ? 1 : 0;
    });
  }, [estudiantes, ordenamiento]);

  // Calcular estudiantes paginados
  const estudiantesPaginados = useMemo(() => {
    const inicio = (paginaActual - 1) * REGISTROS_POR_PAGINA;
    const fin = inicio + REGISTROS_POR_PAGINA;
    return estudiantesOrdenados.slice(inicio, fin);
  }, [estudiantesOrdenados, paginaActual]);

  // Calcular total de páginas
  const totalPaginas = Math.ceil(estudiantesOrdenados.length / REGISTROS_POR_PAGINA);

  const handleOrdenamiento = (campo) => {
    setOrdenamiento((prev) =>
      prev.campo === campo
        ? { campo, direccion: prev.direccion === 'asc' ? 'desc' : 'asc' }
        : { campo, direccion: 'asc' }
    );
  };
  const getOrdenIcon = (campo) => {
    if (ordenamiento.campo !== campo) return '↕️';
    return ordenamiento.direccion === 'asc' ? '↑' : '↓';
  };

  const handleCambio = (idEg, campo, valor) => {
    setCalificaciones((prev) => {
      const next = { ...prev };
      const fila = { ...(next[idEg] || {}) };

      if (campo === 'observaciones') {
        fila.observaciones = valor;
      } else if (campo === 'FINAL') {
        // FINAL es de solo lectura: se calcula automáticamente.
        // Ignoramos cualquier intento de edición manual.
        return prev;
      } else {
        // Solo enteros 0..10 o cadena vacía
        if (valor === '' || valor === null || valor === undefined) {
          fila[campo] = '';
        } else {
          const n = parseInt(valor, 10);
          if (!isNaN(n) && n >= 0 && n <= 10) {
            fila[campo] = String(n);
          }
          // Si no es válido, ignorar
        }
      }

      // Recalcular FINAL automáticamente si cambió un parcial.
      if (campo === 'PARCIAL1' || campo === 'PARCIAL2' || campo === 'PARCIAL3') {
        fila.FINAL = calcularFinalAuto(
          fila.PARCIAL1,
          fila.PARCIAL2,
          fila.PARCIAL3
        );
      }

      next[idEg] = fila;
      return next;
    });
  };

  // Pegar columna desde Excel (multi-fila para una sola columna parcial)
  const handlePaste = (e, estudianteIndex, parcialKey) => {
    const pastedText = e.clipboardData.getData('text');
    const valores = pastedText
      .split(/[\n\r\t]+/)
      .map((v) => v.trim())
      .filter((v) => v !== '');
    if (valores.length > 1) {
      e.preventDefault();
      setCalificaciones((prev) => {
        const next = { ...prev };
        valores.forEach((val, i) => {
          const idx = estudianteIndex + i;
          if (idx < estudiantesOrdenados.length) {
            const est = estudiantesOrdenados[idx];
            const n = parseInt(val, 10);
            if (!isNaN(n) && n >= 0 && n <= 10) {
              const filaPrev = next[est.id_estudiante_grupo] || {};
              // FINAL no acepta pegado manual.
              if (parcialKey === 'FINAL') return;
              const filaNueva = {
                ...filaPrev,
                [parcialKey]: String(n),
              };
              if (
                parcialKey === 'PARCIAL1' ||
                parcialKey === 'PARCIAL2' ||
                parcialKey === 'PARCIAL3'
              ) {
                filaNueva.FINAL = calcularFinalAuto(
                  filaNueva.PARCIAL1,
                  filaNueva.PARCIAL2,
                  filaNueva.PARCIAL3
                );
              }
              next[est.id_estudiante_grupo] = filaNueva;
            }
          }
        });
        return next;
      });
    }
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setMensaje({ tipo: '', texto: '' });
    try {
      const payload = {
        id_grupo: grupo.id_grupo,
        calificaciones: estudiantes.map((est) => {
          const fila = calificaciones[est.id_estudiante_grupo] || {};
          const parciales = {};
          // Solo enviamos las columnas visibles (Extraordinario se omite
          // automáticamente cuando la sección no es Preparatoria) y solo
          // valores no vacíos -> el backend ignora claves ausentes.
          columnas.forEach(({ key }) => {
            const v = fila[key];
            if (v !== '' && v !== null && v !== undefined) {
              parciales[key] = v;
            }
          });
          return {
            id_estudiante_grupo: est.id_estudiante_grupo,
            parciales,
            observaciones: fila.observaciones || '',
          };
        }),
      };

      const resultado = await guardarCalificacionesMulti(payload);
      notifyCalificacionesUpdated(estudiantes.map((est) => est.id_estudiante));
      setMensaje({
        tipo: 'success',
        texto:
          resultado.message ||
          `Se guardaron ${resultado.total_operaciones || 0} calificaciones`,
      });
    } catch (error) {
      console.error('[RegistroGrupoMulti] Error guardando:', error);
      setMensaje({
        tipo: 'error',
        texto: error.message || 'Error al guardar calificaciones',
      });
    } finally {
      setGuardando(false);
    }
  };

  // Reporte: mismo formato que RegistroCalificacionesScreen
  const handleAbrirReporteLocal = () => {
    const datos = estudiantesOrdenados.map((est) => {
      const fila = calificaciones[est.id_estudiante_grupo] || {};
      return {
        id: est.id_estudiante_grupo,
        nombre: est.nombre,
        matricula: est.matricula,
        a1: '',
        ex1: '',
        parcial1: fila.PARCIAL1 || '',
        a2: '',
        ex2: '',
        parcial2: fila.PARCIAL2 || '',
        a3: '',
        ex3: '',
        parcial3: fila.PARCIAL3 || '',
        promedio: fila.FINAL || '',
        observaciones: fila.observaciones || '',
      };
    });
    onAbrirReporte(datos);
  };

  return (
    <div className={styles.registroContainer}>
      {/* Card de cabecera (similar al de Registro de Calificaciones) */}
      <div className={styles.busquedaCard}>
        <div className={styles.busquedaHeader}>
          <div className={styles.busquedaIcono}>
            <span>🔍</span>
          </div>
          <h2 className={styles.busquedaTitulo}>Búsqueda de Grupo</h2>
        </div>

        <div className={styles.filtrosContainer}>
          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Campus</label>
            <select className={styles.filtroSelect} disabled>
              <option>{grupo?.campus || 'Sin campus'}</option>
            </select>
          </div>
          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Programa</label>
            <select className={styles.filtroSelect} disabled>
              <option>{grupo?.programa_academico || grupo?.programa || 'Sin programa'}</option>
            </select>
          </div>
        </div>

        <div className={styles.botonesAccion}>
          <button className={styles.regresarButton} onClick={onRegresar}>
            REGRESAR
          </button>
        </div>
      </div>

      {/* Card del grupo + tabla de captura múltiple */}
      <div className={styles.grupoCard}>
        <div className={styles.grupoHeader}>
          <div className={styles.grupoIcono}>
            <span>👥</span>
          </div>
          <div className={styles.grupoInfo}>
            <h3 className={styles.grupoTitulo}>
              Grupo: {grupo?.codigo} - {grupo?.nombre}
            </h3>
            <p className={styles.grupoHorario}>
              Horario: {grupo?.horario || 'Sin horario asignado'}
            </p>
          </div>
        </div>

        {mensaje.texto && (
          <div
            className={
              mensaje.tipo === 'error'
                ? styles.errorMessage
                : styles.successMessage
            }
          >
            {mensaje.texto}
          </div>
        )}

        <div className={styles.tablaCalificacionesContainer}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <p>Cargando estudiantes...</p>
            </div>
          ) : estudiantes.length === 0 ? (
            <div className={styles.emptyContainer}>
              <p>No hay estudiantes inscritos en este grupo.</p>
            </div>
          ) : (
            <table className={styles.tablaCalificaciones}>
              <thead>
                <tr className={styles.tablaCalificacionesHeader}>
                  <th
                    onClick={() => handleOrdenamiento('nombre')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Estudiante {getOrdenIcon('nombre')}
                  </th>
                  <th
                    onClick={() => handleOrdenamiento('matricula')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Matrícula {getOrdenIcon('matricula')}
                  </th>
                  {columnas.map((p) => (
                    <th key={p.key}>{p.label}</th>
                  ))}
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {estudiantesPaginados.map((est, index) => {
                  const fila = calificaciones[est.id_estudiante_grupo] || {};
                  return (
                    <tr
                      key={est.id_estudiante_grupo}
                      className={styles.tablaCalificacionesFila}
                    >
                      <td className={styles.estudianteNombre}>{est.nombre}</td>
                      <td>{est.matricula}</td>
                      {columnas.map((p) => {
                        const isFinal = p.key === 'FINAL';
                        return (
                          <td key={p.key}>
                            <input
                              type="text"
                              inputMode="numeric"
                              className={styles.inputCalificacion}
                              value={fila[p.key] ?? ''}
                              readOnly={isFinal}
                              tabIndex={isFinal ? -1 : 0}
                              title={
                                isFinal
                                  ? 'Calificación Final calculada automáticamente: (P1+P2+P3)/3 con redondeo por rangos'
                                  : undefined
                              }
                              style={
                                isFinal
                                  ? {
                                      backgroundColor: '#f3f4f6',
                                      color: '#374151',
                                      cursor: 'not-allowed',
                                      fontWeight: 'bold',
                                    }
                                  : undefined
                              }
                              onChange={(e) => {
                                if (isFinal) return;
                                handleCambio(
                                  est.id_estudiante_grupo,
                                  p.key,
                                  e.target.value
                                );
                              }}
                              onPaste={(e) => {
                                if (isFinal) {
                                  e.preventDefault();
                                  return;
                                }
                                handlePaste(e, index, p.key);
                              }}
                              onKeyDown={(e) => {
                                if (isFinal) {
                                  // Permitir solo navegación; bloquear edición.
                                  if (
                                    !['Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(
                                      e.key
                                    )
                                  ) {
                                    e.preventDefault();
                                  }
                                  return;
                                }
                                if (e.ctrlKey || e.metaKey) return;
                                if (
                                  [
                                    'Backspace',
                                    'Delete',
                                    'Tab',
                                    'ArrowLeft',
                                    'ArrowRight',
                                    'Home',
                                    'End',
                                  ].includes(e.key)
                                )
                                  return;
                                if (!/^\d$/.test(e.key)) e.preventDefault();
                              }}
                              placeholder={isFinal ? '—' : '0-10'}
                            />
                          </td>
                        );
                      })}
                      <td>
                        <input
                          type="text"
                          className={styles.inputObservaciones}
                          value={fila.observaciones || ''}
                          onChange={(e) =>
                            handleCambio(
                              est.id_estudiante_grupo,
                              'observaciones',
                              e.target.value
                            )
                          }
                          placeholder=""
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Controles de Paginación */}
        {totalPaginas > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '12px', 
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <button
              onClick={() => setPaginaActual(prev => Math.max(1, prev - 1))}
              disabled={paginaActual === 1}
              style={{
                padding: '8px 12px',
                backgroundColor: paginaActual === 1 ? '#d1d5db' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: paginaActual === 1 ? 'not-allowed' : 'pointer',
                fontWeight: '600'
              }}
            >
              ← Anterior
            </button>
            <span style={{ fontWeight: '600', color: '#374151' }}>
              Página {paginaActual} de {totalPaginas} (Total: {estudiantesOrdenados.length} estudiantes)
            </span>
            <button
              onClick={() => setPaginaActual(prev => Math.min(totalPaginas, prev + 1))}
              disabled={paginaActual === totalPaginas}
              style={{
                padding: '8px 12px',
                backgroundColor: paginaActual === totalPaginas ? '#d1d5db' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: paginaActual === totalPaginas ? 'not-allowed' : 'pointer',
                fontWeight: '600'
              }}
            >
              Siguiente →
            </button>
          </div>
        )}

        <div className={styles.botonesInferiores}>
          <button
            className={styles.guardarButton}
            onClick={handleGuardar}
            disabled={guardando || loading || estudiantes.length === 0}
          >
            {guardando ? 'GUARDANDO...' : 'GUARDAR CALIFICACIONES'}
          </button>
          <button
            className={styles.reporteButton}
            onClick={handleAbrirReporteLocal}
            disabled={loading || estudiantes.length === 0}
          >
            REPORTE DE CALIFICACIONES
          </button>
        </div>
      </div>
    </div>
  );
}

export default RegistroGrupoMulti;
